import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  viewChild,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { PlotlyComponent, PlotlyService, PlotlySharedModule } from 'angular-plotly.js';
import { debounce, groupBy, range, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { Layout, LayoutAxis, PlotData, PlotHoverEvent, PlotlyHTMLElement } from 'plotly.js';
import { BehaviorSubject, combineLatest, EMPTY, from, fromEvent, interval, merge, Observable, of, OperatorFunction, ReplaySubject, Subject, Subscription } from 'rxjs';
import {
  bufferWhen,
  catchError,
  debounceTime,
  delayWhen,
  distinctUntilChanged,
  filter,
  first,
  map, mergeMap,
  retry,
  share,
  startWith,
  switchMap,
  tap,
  throttleTime
} from 'rxjs/operators';
import { IConfigLoadedSeriesData } from 'src/app/model/config-loaded-series-data';
import { IDataSeries, isSameSubSeries } from 'src/app/model/data-series';

import { IGraphConfig } from 'src/app/model/graph-configs/graph-config';
import {
  ExtendedPlotlyFullData,
  ExtendedPlotlyFullLayout
} from 'src/app/model/plotly/plotly-type-extensions';
import { ITab } from 'src/app/model/tab';
import { GraphConfigSeriesDataLoaderService } from 'src/app/services/data/series-data-loader/graph-config-series-data-loader.service';
import { LogService } from 'src/app/services/loggers/logger.service';
import { PlotlyGeneratorProviderService } from 'src/app/services/plotly-generator/plotly-generator-provider.service';
import { UINotificationService } from 'src/app/services/ui-notification/uinotification.service';
import { ArrayUtils } from 'src/app/utility/array-utils';
import { deepCopySync } from 'src/app/utility/deep-copy';
import { createDefaultTab } from 'src/app/utility/default-object-creator';
import {
  ExtendTracesRequest,
  PlotDataWithIdentification,
  PlotlyLayout,
  PlotlyTraceGenerator
} from 'src/app/utility/input-converter/plotly-trace-generator';

import { AxisName_T, ILegend, PlotlyLayoutGenerator } from 'src/app/utility/layout-processor/plotly-layout-generator';
import { PlotlyConfigurationGenerator } from 'src/app/utility/plotly-configuration-generator/plotly-configuration-generator';
import { DefaultConfig } from 'src/assets/default.config';
import { SubSink } from 'subsink';

import { isAnnotationSeries } from 'src/app/model/annotation-series';
import { GraphAgentContextService } from 'src/app/services/agent/graph-agent-context.service';
import { CopilotSidebarService } from 'src/app/services/copilot-sidebar/copilot-sidebar.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';

import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { IonIcon } from '@ionic/angular/standalone';
import { PlotlyHoverTimeTooltipDirective } from 'src/app/directives/plotly/plotly-hover-time-tooltip.directive';
import { ResetZoomButtonsDirective } from 'src/app/directives/plotly/reset-zoom-buttons/reset-zoom-buttons.directive';
import { SafeResizeHandlerDirective } from 'src/app/directives/plotly/reset-zoom-buttons/safe-resize-handler.directive';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { isArraySeries, isScatterSeries } from 'src/app/model/values-config';
import { IConfigSeriesDataLoader } from 'src/app/services/data/series-data-loader/helper/config-series-data-loader-interface';
import { Offsetting } from 'src/app/utility/offsetting';
import { absolutePlotlyDateToDateTime, getAxesFromSubplotKey, getAxisLongFromShortName, isAxisName, loadPlotly, relativeLinearToDateTime } from 'src/app/utility/plotly';
import { determineDataEarliestAndLatest } from 'src/app/utility/plotly/determine-data-earliest-and-latest';
import { clampRangeToDistinctTicks, generateRelativeTimeTicks } from 'src/app/utility/plotly/relative-time-tick-formatter';
import { RelativeTimeBaseDateService } from 'src/app/utility/relative-time-base-date.service';
import { throttleTimeBatched } from 'src/app/utility/rxjs';
import { findSharedBaseDate, getSeriesThingiesAndBaseDates } from 'src/app/utility/series/base-date';
import { PureFunctionCallPipe } from '../../utility/pipes/pure-member-call/pure-member-call.pipe';
import { StringEllipsisComponent } from '../string-ellipsis/string-ellipsis.component';


/**
 * Information about a series in the legend.
 */
interface ILegendSeriesInfo {
  group: string;
  seriesName: string;
  series: PlotDataWithIdentification;
  seriesModificationInfo: string;
}


/**
 * Group of related (depending on grouping) series in the legend.
 */
interface ILegendSeriesGroup {
  name: string;
  seriesList: ILegendSeriesInfo[];
}


interface IIndicesAndStyles {
  markerSizes: number[];
  indices: number[];
  lineWidth: number;
}

enum StyleVariant {
  Normal,
  Highlighted
}





/**
 * Helper, which checks the type of value and converts it to milliseconds.
 *
 * This will be used to convert ISO strings made e.g. by the
 * "small time fix" to number.
 * (@see {@link ScatterPlotTraceGenerator}'s fixSmallTimeValues() )
 *
 * Should only be used on values which are times
 * in either milliseconds or are strings in ISO format,
 * as only the types are checked, not the actual value.
 *
 * @param val the value to check
 * @returns the value in milliseconds
 */
function toMillis(val: number | string | Date): number {
  if (typeof val === 'number') {
    return val;
  }
  if (val instanceof Date) {
    return val.valueOf();
  }

  return DateTime.fromISO(val).toMillis();
}

@Component({
  selector: 'app-chart-view-tab',
  templateUrl: './chart-view-tab.component.html',
  styleUrls: ['./chart-view-tab.component.scss'],
  imports: [
    MaterialImportsModule,
    NgTemplateOutlet,
    PlotlySharedModule,
    ResetZoomButtonsDirective,
    PlotlyHoverTimeTooltipDirective,
    SafeResizeHandlerDirective,
    IonIcon,
    AsyncPipe,
    PureFunctionCallPipe,
    StringEllipsisComponent
  ],
  providers: [RelativeTimeBaseDateService]
})
export class ChartViewTabComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('thePlot')
  private thePlot?: PlotlyComponent;

  @ViewChild('thePlot', { read: ElementRef })
  private thePlotHtmlElement?: ElementRef<HTMLElement>;

  @ViewChild(ResetZoomButtonsDirective)
  private zoomButtons?: ResetZoomButtonsDirective;

  // legend resize observing, which triggers plot resize
  @ViewChildren('legendwrapper')
  private legendwrappers!: QueryList<ElementRef<HTMLElement>>;
  private legendResized = new Subject<void>();
  private legendResizeObserver = new ResizeObserver(() => {
    this.legendResized.next();
  });
  private observedLegendwrappers = new Set<HTMLElement>();

  @ViewChild('plotWrapper', { static: false })
  private plotWrapper?: ElementRef<HTMLDivElement>;

  private readonly flexContainerAroundWrapper = viewChild
    .required<string, ElementRef<HTMLDivElement>>('flexContainerAroundWrapper', { read: ElementRef });


  @Input({ required: true })
  public set tab(newTab: ITab) {
    this._tab = Object.assign({}, newTab);
    this._tab.graphConfig  = deepCopySync(this.tab.graphConfig);
    // use the same object for _extraData of newTab and this._tab, so that extraData can be shown in the parent component
    this._tab._extraData = newTab._extraData ?? {};
    newTab._extraData = this._tab._extraData;

    setTimeout(() => void this.refreshGraph());
  }

  public get tab() {
    return this._tab;
  }

  // the backing field for the tab property
  private _tab: ITab = createDefaultTab();

  @Input()
  public bufferingPeriod = DefaultConfig.chartView.liveDataBufferingTimespan;

  @Input()
  public legendPosition: 'top' | 'bottom' | undefined = 'bottom';

  @Input()
  public staticData = false;

  @Output()
  public chartIsLoaded = new EventEmitter();

  private offsetFinished$ = new BehaviorSubject(false);

  /**
   * Revision counter used to tell plotly when to update the plot
   */
  private _plotRevision = new BehaviorSubject(0);
  private _highPriorityPlotUpdateEvent = new Subject<void>();
  protected plotRevision$ = merge(
    this._plotRevision.pipe(throttleTime(1000, undefined, { leading: true, trailing: true })),
    this._highPriorityPlotUpdateEvent.pipe(switchMap(() => this._plotRevision.pipe(first())))
  ).pipe(distinctUntilChanged(), tap(() => {
    this.markPlotRenderPending();
    // Before updating, quickly store some layout changes due to user clicks on plotly modebar in the new layout.
    // Clicking in the mode bar causes the plotlyDiv's layout to update, but this is not reflected
    // in the layout of the plotly-plot component or the `this.tab.layout`.
    if (this.tab.layout) {
      this.tab.layout.hovermode = (this.thePlot?.plotlyInstance as PlotlyHTMLElement | undefined)?.layout?.hovermode;
      this.tab.layout.dragmode = (this.thePlot?.plotlyInstance as PlotlyHTMLElement | undefined)?.layout?.dragmode;
    }
  }));

  protected plotRenderReady = false;
  private plotRenderSettledTimeout?: ReturnType<typeof setTimeout>;
  public highPriorityIncrement() {
    this.plotRevision += 1;
    this._highPriorityPlotUpdateEvent.next();
  }

  private get plotRevision() {
    return this._plotRevision.value;
  }
  private set plotRevision(newRevision: number) {
    this._plotRevision.next(newRevision);
  }
  protected recreatePlotEvent$ = new BehaviorSubject({});


  /**
   * Indicates that no data is curretly present
   * and a default layout needs to be used
   */
  private _noDataPresent = true;
  public get noDataPresent() {
    return this._noDataPresent;
  }
  public set noDataPresent(newVal: boolean) {
    if (this._noDataPresent !== newVal) {
      this._noDataPresent = newVal;
      this.plotRevision++;
    }
  }

  private liveOffsetRequested$ = new Subject<'request' | 'reset'>();

  /**
   * Error message from loading data, to be displayed over the graph
   */
  protected errorMessage?: string;

  protected get plotDataState(): 'ready' | 'loading' | 'no-data' | 'no-series' | 'error' {
    if (this.errorMessage !== undefined) {
      return 'error';
    }

    if (!this.noDataPresent) {
      return 'ready';
    }

    if (this.tab.graphConfig.internalSeriesList.length === 0) {
      return 'no-series';
    }

    if (this.tab.outputData.length > 0) {
      return 'no-data';
    }

    return 'loading';
  }

  /**
   * Default layout to be used when no data is present
   */
  protected emptyDataLayout: PlotlyLayout = {
    margin: DefaultConfig.chartView.layout.margin
  };

  private mouseMove$ = new Subject<{ event: MouseEvent; outputData: PlotDataWithIdentification[] }>();

  /**
   * When this subject emits, all reset buttons in the graph will be removed
   */
  protected removeResetButtons$ = new Subject<void>();

  private ancestorIsScrolling = false;

  private traceGenerator?: PlotlyTraceGenerator;
  private configGenerator?: PlotlyConfigurationGenerator;
  private layoutGenerator?: PlotlyLayoutGenerator;

  private extendTracesQueue = new Subject<PlotDataWithIdentification>();

  /**
   * Series that have been replaced since the last plot data update.
   * Updates for these series need to be filtered out from buffered updates because they might be stale.
   */
  private replacedSeriesSinceUpdate: PlotDataWithIdentification[] = [];

  /**
   * Immediately trigger the extendTracesQueue buffer, so that stale items that are currently buffered can be removed by the following filter stage
   */
  private triggerExtendedTracesQueue$ = new Subject<void>();

  private subscriptions = new SubSink();
  private configUpdateSubscription!: Subscription | null;
  private dataGraphConfig$ = new Subject<IGraphConfig>();

  private traceDataMap = new Map<IDataSeries, TraceData>();

  /**
   * The group or series that is currently focused.
   *
   * Series not within the group are hidden from the graph, but
   * are still shown in the legend.
   */
  private focusedGroupOrSeries: ILegendSeriesGroup | ILegendSeriesInfo | null = null;


  //// --- Line widths for highlighting --- ////
  private extraWeight = DefaultConfig.chartView.incrementValue;
  private lineSizeDefault = DefaultConfig.chartView.defaultLineWidth;
  private lineSizeHighlighted = DefaultConfig.chartView.defaultLineWidth + this.extraWeight;
  private markerSizeDefault = DefaultConfig.chartView.markerSize;
  private markerSizeHighlighted = DefaultConfig.chartView.markerSize + this.extraWeight;
  private annotationMarkerSize = DefaultConfig.chartView.annotationMarkerSize;
  private annotationMarkerSizeHighlighted = DefaultConfig.chartView.annotationMarkerSize + this.extraWeight;
  //// ----------------------------------- ////


  /**
   * Will have un-/hover events passed to it from the plotly component.
   *
   * @private
   */
  protected readonly hoverEvent$ = new Subject<{ type: 'hover' | 'unhover'; event: PlotHoverEvent }>();


  constructor(
    private elementRef: ElementRef,
    private generatorProviderService: PlotlyGeneratorProviderService,
    private log: LogService,
    private plotlyService: PlotlyService,
    private scrollDispatcher: ScrollDispatcher,
    private seriesDataLoader: GraphConfigSeriesDataLoaderService,
    private uiNotifications: UINotificationService,
    private thingieService: ThingieService,
    private relativeDateService: RelativeTimeBaseDateService,
    private graphAgentContextService: GraphAgentContextService,
    private copilotSidebarService: CopilotSidebarService,
  ) {
    void loadPlotly();

    this.setLayoutRanges = debounce(this.setLayoutRanges.bind(this), 50) as () => Promise<void>;
    this.handleAutoOffsetRequestedEvent = this.handleAutoOffsetRequestedEvent.bind(this);
  }


  public ngOnInit(): void {
    this.subscriptions.sink = this.scheduleExtendTraces();

    this.subscriptions.sink = this.mouseMove$.pipe(throttleTime(20)).subscribe(event => {
      void this.highlightCurve(event);
    });

    this.setupLiveOffsetTriggerLogic();

    this.subscriptions.sink = this.legendResized.subscribe(() => {
      this.updateVisualDistanceBuffer();
    });
  }

  public ngAfterViewInit() {
    if (!this.staticData) {
      this.subscribeData();
    }
    // track size changes to all legendwrappers using a ResizeObserver
    this.subscriptions.sink = this.legendwrappers.changes.pipe(
      startWith(undefined)
    )
      .subscribe(() => {
        // observe new elements
        const newWrapperElements = this.legendwrappers.map(wrapper => {
          if (!this.observedLegendwrappers.has(wrapper.nativeElement)) {
            this.legendResizeObserver.observe(wrapper.nativeElement);
            this.observedLegendwrappers.add(wrapper.nativeElement);
            this.legendResized.next();
          }
          return wrapper.nativeElement;
        });

        // unobserve removed elements
        [...this.observedLegendwrappers.values()]
          .filter(el => !newWrapperElements.includes(el))
          .forEach(el => {
            this.legendResizeObserver.unobserve(el);
            this.observedLegendwrappers.delete(el);
          });
      });

    this.subscriptions.sink = merge(this.legendResized, fromEvent(window, 'resize')).pipe(
      startWith(undefined),
      // Choose a debounce higher than plotly.js' internal debounce
      // to prevent the legend from overlapping the plot (actually plotly does not
      // resize it's charting area accordingly so it runs into hidden overflow).
      // Testing with 5000 ms, the problem seemingly did not occur in the lab.
      // Testing with 150 ms, the problem mostly did not occur in the lab.
      // => We are not sure if the problem is fixed, but we are confident enough using a number > 150 ms.
      // (see COSOFT-2768 and its related merge request).
      debounceTime(333)
    )
      .subscribe(() => {
        this.onNewChartViewAreaSize().catch(_ => { /* noop */ });
      });

    // Disable legend's hover-actions when an ancestor is being scrolled
    this.subscriptions.sink = this.scrollDispatcher.ancestorScrolled(this.elementRef)
      .pipe(
        tap(() => this.ancestorIsScrolling = true),
        debounceTime(DefaultConfig.general.syntheticScrollEndDelay_Milliseconds),
        tap(() => this.ancestorIsScrolling = false)
      )
      .subscribe();


    this.disableRightClickInteractionsOnChart();
  }

  public ngOnDestroy(): void {
    if (this.configUpdateSubscription) {
      this.configUpdateSubscription.unsubscribe();
      this.configUpdateSubscription = null;
    }

    if (this.plotRenderSettledTimeout !== undefined) {
      clearTimeout(this.plotRenderSettledTimeout);
      this.plotRenderSettledTimeout = undefined;
    }

    this.relativeDateService.clearAll();
    this.subscriptions.unsubscribe();
  }

  private markPlotRenderPending() {
    this.plotRenderReady = false;
    if (this.plotRenderSettledTimeout !== undefined) {
      clearTimeout(this.plotRenderSettledTimeout);
      this.plotRenderSettledTimeout = undefined;
    }
  }

  protected onAfterPlot() {
    this.markPlotRenderPending();
    this.plotRenderSettledTimeout = setTimeout(() => {
      this.plotRenderReady = true;
      this.plotRenderSettledTimeout = undefined;
    }, 100);
  }

  private setupLiveOffsetTriggerLogic() {
    this.subscriptions.sink = this.dataGraphConfig$.pipe(switchMap(_graphConfig =>
      this.liveOffsetRequested$.pipe(
        // liveOffsetRequested can be triggered with a "request to offset", or with "reset".
        // only process the first of many concurrent "request"s, until the current request is finished and a "reset" is triggered
        distinctUntilChanged(),
        filter(requested => requested === 'request'),
        // do the offsetting
        switchMap(() => {
          const request = Offsetting.requestFromGraphConfig(_graphConfig);
          return from(Offsetting.handleAutoOffsetRequestedEvent(request, this.seriesDataLoader));
        }),
        map(() => {
          // offsetting done.
          // reprocess all series that are already rendered
          this.traceDataMap.forEach(traceData => traceData.reprocessData());
          // release data points that are waiting for offsetting to finish
          this.offsetFinished$.next(true);

        }),
        // if it fails, just try again next time
        catchError((error: unknown) => {
          this.log.error('Live offset calculation error:', error);

          return of(null);
        }),
        tap(() => {
          // allow to retrigger offsetting by future requests
          this.offsetFinished$.next(false);
          this.liveOffsetRequested$.next('reset');
        })
      ))).subscribe();
  }

  /**
   * Trigger live offsetting after the given series received data, but only if no offset has been calculated yet for the series
   *
   * @param _graphConfig graph config
   * @param seriesIdx series that received data
   * @returns Observable that emits true when offsetting is finished or when no offsetting is necessary
   */
  private handleAutoOffsetRequestedEvent(_graphConfig: IGraphConfig, seriesIdx: number): Observable<boolean> {
    const series = _graphConfig.internalSeriesList[seriesIdx];
    if (isScatterSeries(series)) {
      // check if offset is already calculated
      if (series.valueConfig.yAxisOffset === undefined) {
        // trigger offsetting
        this.liveOffsetRequested$.next('request');
        return this.checkIfOffsetIsFinished();
      }
    }
    // no offsetting necessary
    return of(true);
  }

  private checkIfOffsetIsFinished() {
    return this.offsetFinished$.pipe(
      filter(isFinished => isFinished === true),
      first()
    );
  }


  private subscribeData() {
    const dataLoader = this.seriesDataLoader.getDataLoader();


    // when graph config changes, transform each series into a TraceData object
    // that is responsible for requesting series data from the backend and supplies
    // initial as well as live data
    const traceDatas$ = this.dataGraphConfig$.asObservable().pipe(
      switchMap(graphConfig => {
        if (graphConfig.timeReference !== 'RELATIVE') {
          return of({ graphConfig, sharedBaseDate: undefined as string | undefined });
        }
        return getSeriesThingiesAndBaseDates(graphConfig, this.thingieService).pipe(
          map(({ seriesBaseDates }) => ({
            graphConfig,
            sharedBaseDate: findSharedBaseDate(graphConfig.internalSeriesList, seriesBaseDates),
          })),
          catchError(() => of({ graphConfig, sharedBaseDate: undefined }))
        );
      }),
      map(({ graphConfig, sharedBaseDate }) => {
        // cancel all subscriptions to the old series
        this.traceDataMap.forEach(value => value.destroy());
        this.traceDataMap.clear();
        this.relativeDateService.setBaseDate('chart-base-date', sharedBaseDate);
        const traceDatas: TraceData[] = [];

        if (graphConfig.autoOffsets.mode !== 'LIVE') {
          this.offsetFinished$.next(true);
        } else {
          this.offsetFinished$.next(false);
        }

        for (const [idx, series] of graphConfig.internalSeriesList.entries()) {
          if (graphConfig.autoOffsets.mode === 'LIVE' && isScatterSeries(series)) {
            series.valueConfig.yAxisOffset = undefined;
          }
          const traceData = new TraceData(
            this.tab,
            graphConfig,
            series,
            dataLoader,
            this.traceGenerator!,
            () => this.handleAutoOffsetRequestedEvent(graphConfig, idx),
            sharedBaseDate,
          );
          traceDatas.push(traceData);
          // initially show full range
          traceData.setRange({});
          this.traceDataMap.set(series, traceData);
        }

        return traceDatas;
      }),
      share()
    );
    this.subscriptions.sink = traceDatas$.pipe(
      switchMap(traceDatas =>
        // setup three triggers for plotting:
        merge(
          // if shown data changes, replace existing series. Don't do a high priority
          // increment, so we don't rerender the plot 48 times for 48 series
          from(traceDatas)
            .pipe(
              mergeMap(traceData => traceData.shownData$),
              map(data => ({ datas: [data], replace: true, highPriority: false }))
            ),
          // after all data series got new data, trigger a high priority increment
          // to immediately update the plot. This shows the plot a little faster
          // than waiting one second for the plotrevision debounce.
          // No need to include the data, because it has already been processed by
          // the first trigger.
          combineLatest(traceDatas.map(traceData => traceData.shownData$))
            .pipe(
              debounceTime(200),
              map(() => ({ datas: [], replace: false, highPriority: true })),
              tap(() => this.traceDataMap.forEach(traceData => traceData.updateVirtualEndpoint())),
            ),
          // when live data comes in, amend it to the plot (unless replace is requested)
          from(traceDatas)
            .pipe(
              mergeMap(traceData => traceData.liveData$),
              map(data => ({ datas: [[data]], replace: data.replace, highPriority: false })),
              tap(() => this.traceDataMap.forEach(traceData => traceData.updateVirtualEndpoint())),
            ),
          from(traceDatas)
            .pipe(
              mergeMap(traceData => traceData.updateVirtualEndpoint$),
              map(data => ({ datas: [[data]], replace: false, highPriority: false }))
            )
        ))
    )
      .pipe(tap(({ datas, replace, highPriority }) => {
        if (highPriority) {
          this.highPriorityIncrement();
        }
        datas.forEach(newSeries => newSeries.forEach(subSeries =>
          this.updateScatterPlot(subSeries, replace)));
        this.chartIsLoaded.emit();
      }))
      .pipe(tap(() => {
        this.errorMessage = undefined;
      }))
      .pipe(
        catchError((err: unknown) => {
          // handle some common error cases
          const errorCause = (err as any)?.error?.traces?.[1]?.[0];
          if (errorCause?.includes('NonMonotonicSequenceException')) {
            this.errorMessage = 'Calibration not possible, because the trends of online data points and offline data do not match. '
            + 'Please revise your offline values or disable calibration.';
          } else if (errorCause?.includes('TooFewOfflinePointsException')) {
            const minAmount = /Need at least (\d+)\./.exec(errorCause)?.[1];
            const minAmountString = minAmount !== undefined ? 'at least ' + minAmount : 'more';
            this.errorMessage = 'The selected calibration file requires ' + minAmountString + ' offline points that are not more than 5 minutes before or after the series. '
            + 'Please add offline values or disable calibration.';
          } else if (errorCause?.includes('OutOfRangeException')) {
          // Error while executing dataprocessors: Caused by error (OutOfRangeException)
            const outOfRangeMessage = errorCause.split(':')[2].split(' ');
            this.errorMessage = 'Offline value ' + outOfRangeMessage[0]
          + ' used for calibration is out of the range ' + outOfRangeMessage[3] + outOfRangeMessage[4] + ' of the calibration curve.'
          + ' Please revise your offline values';
          } else {
            this.displayError('Failed to load data', err);
          }
          throw err;
        }),
        retry()
      )
      .subscribe();
  }

  private displayError(message: string, error: any): void {
    this.uiNotifications.displayErrorAlert({
      title: 'Error',
      message,
      details: error instanceof Error ? error.message : JSON.stringify(error)
    }, error);
  }

  private updateScatterPlot(plotlyData: PlotDataWithIdentification, replace: boolean) {
    if (this.traceGenerator === undefined) {
      this.log.debug(`No trace generator available. Won't update the scatter plot`);
      return;
    }
    if (plotlyData.subseries === 'calibrationWarning') {
      this.tab._extraData.calibrationWarning = plotlyData.y![plotlyData.y!.length - 1] as any;
      return;
    }

    const outputData = this.tab.outputData;
    const matchingSeriesIndices = [...outputData.keys()]
      .filter(i => isSameSubSeries(outputData[i], plotlyData) &&
      outputData[i].subseries === plotlyData.subseries);

    if (matchingSeriesIndices.length === 0) {
      // this has to be a new series!
      this.tab.outputData = [...this.tab.outputData, plotlyData];
      ++this.plotRevision;
    }

    for (const i of matchingSeriesIndices) {
      if (replace === true) {
        // first shallow copy, then modify, such that the existing
        // plot is not altered until the plotRevision observable has emitted
        this.tab.outputData = [...this.tab.outputData];
        // save the last visible state for the series
        const lastVisibleState = this.tab.outputData[i].visible;
        if ( lastVisibleState === false) {
          plotlyData.visible = lastVisibleState;
        }
        this.tab.outputData[i] = plotlyData;
        ++this.plotRevision;

        // remove items from the extendTracesQueue that match this series, because they are now outdated and should not be added anymore.
        this.replacedSeriesSinceUpdate.push(plotlyData);
        this.triggerExtendedTracesQueue$.next();
      } else {
        // 1) Check if new data point is older than the last data point added
        // 2a) if it's not older, continue as usual
        // 2b) if it's older, "re-order" data and run the replace-action in if(... == true)

        const currentXData = this.tab.outputData[i].x as number[];
        const lastXValue = ArrayUtils.endValue(currentXData);

        // Chronologically sort data to be added
        const sortedIncomingData = (plotlyData.x as (number | string)[]).map(
          (x, i) => ({ x, y: plotlyData.y![i] })
        ).sort((a, b) => toMillis(a.x) - toMillis(b.x));

        if (isArraySeries(plotlyData.series) || toMillis(ArrayUtils.startValue(sortedIncomingData).x) >= toMillis(lastXValue)) {
          this.extendTracesQueue.next(plotlyData);
        } else {
          const existingX = this.tab.outputData[i].x as (number | string)[];
          const existingY = this.tab.outputData[i].y as any[];

          this.insertChronologically(existingX, existingY, sortedIncomingData);
          ++this.plotRevision;
        }
      }
    }

    // If there is no data present - yet - use a modified layout
    // where the x-axis time range lies in the present

    let hasData = false;
    for (const data of this.tab.outputData) {
      if (data.x !== undefined && data.x.length > 0) {
        hasData = true;
        break;
      }
    }

    if (hasData === false) {
      // Use the existing layout to display all series axes already
      const buf = deepCopySync(this.tab.layout!);

      // Adjust the time axis range

      if (!buf.xaxis) {
        buf.xaxis = {};
      }
      let beginning = 0;
      if (this.tab.graphConfig.timeReference === 'ABSOLUTE') {
        beginning = DateTime.now().toMillis();
      }
      const end = beginning + (60 * 60 * 1000);
      buf.xaxis.range = [beginning, end];

      this.emptyDataLayout = buf;

      // force to update the layout
      this.highPriorityIncrement();
    }

    this.noDataPresent = !hasData;

    void this.setLayoutRanges();
  }

  /**
   * Insert the source data into the correct positions into `targetX` & `targetY`
   *
   * Note: modifies `targetX` & `targetY` in-place.
   *
   * `targetX` & `targetY` need to have the same length.
   *
   * @param targetX Array of timestamps
   * @param targetY Array of arbitrary values
   * @param source ascendingly timestamp-sorted array of (x,value)-tuples
   *    to be added to `targetX` and `targetY`
   * @throws Error if `targetX` & `targetY` differ in length
   */
  private insertChronologically(
    targetX: (number | string)[],
    targetY: number[],
    source: { x: number | string; y: any }[]
  ) {
    if (targetX.length !== targetY.length) {
      throw new Error(`timestamp and value arrays differ in length.`);
    }

    // Special case 1: if target is empty or if source values
    // are all newer than target's last value
    if (
      targetX.length === 0 ||
      toMillis(ArrayUtils.startValue(source).x) > toMillis(ArrayUtils.endValue(targetX))
    ) {
      targetX.push(...source.map(d => d.x));
      targetY.push(...source.map(d => d.y));
      return;
    }

    // Special case 2: most-recent source values is older than first target value
    if (toMillis(ArrayUtils.endValue(source).x) < toMillis(ArrayUtils.startValue(targetX))) {
      targetX.unshift(...source.map(d => d.x));
      targetY.unshift(...source.map(d => d.y));
      return;
    }

    let sourceIdx = -1;
    let targetIdx = ArrayUtils.endIndex(targetX);
    let processingFirstValue = true;

    while (++sourceIdx in source) {
      const srcVal = source[sourceIdx];
      let tVal_X = targetX[targetIdx];

      // Special case 1 check
      if (toMillis(srcVal.x) >= toMillis(ArrayUtils.endValue(targetX))) {
        targetX.push(...source.map(d => d.x).slice(sourceIdx));
        targetY.push(...source.map(d => d.y).slice(sourceIdx));
        return;
      }


      // Distinction made between first value and the remaining values
      // because:
      // we ordered the source values chronologically ascending,
      // meaning the position of insertion for the first value
      // represents the "left-most" position for any value of the source
      // set.
      //
      // So, after finding the first value position, we can start
      // searching new positions towards the end of the target array.
      if (processingFirstValue) {
        while (toMillis(srcVal.x) < toMillis(tVal_X)) {
          --targetIdx;
          if (targetIdx === -1) {
            break;
          }
          tVal_X = targetX[targetIdx];
        }

        targetX.splice(targetIdx + 1, 0, srcVal.x);
        targetY.splice(targetIdx + 1, 0, srcVal.y);

        if (targetIdx === -1) {
          // lower bound is 0, so we don't
          // access out-of-bounds and use undefined
          // for comparisons
          targetIdx = 0;
        }

        processingFirstValue = false;
      } else {
        while (toMillis(srcVal.x) >= toMillis(tVal_X)) {
          ++targetIdx;
          if (targetIdx === (ArrayUtils.endIndex(targetX) + 1)) {
            break;
          }
          tVal_X = targetX[targetIdx];
        }

        targetX.splice(targetIdx, 0, srcVal.x);
        targetY.splice(targetIdx, 0, srcVal.y);
      }
    }
  }

  /**
   * Handle adding new data points to existing traces in the plot.
   *
   * Start to buffer the extendTraces requests, combine them into one and call
   * plotly.extendTraces if new data is available.
   * This is because we cannot run plotly.extendTraces as soon as new data
   * has arrived, because that can lead to performance issuses when data is
   * arriving too fast.
   *
   * @returns subscription
   */
  private scheduleExtendTraces() {
    const bufferOperator: OperatorFunction<PlotDataWithIdentification, PlotDataWithIdentification[]> =
      this.bufferingPeriod > 0
        ? bufferWhen(() => merge(interval(this.bufferingPeriod), this.triggerExtendedTracesQueue$))
        : map(data => [data]);

    return this.dataGraphConfig$.asObservable()
      .pipe(
        // re-subscribe the extendTracesQueue every time a new graphConfig arrives,
        // thereby discarding existing data in the buffer
        switchMap(() => this.extendTracesQueue.asObservable().pipe(
          bufferOperator,
          // filter out updates that have been fully replaced since the update was queued
          map(updates => updates.filter(update => !this.replacedSeriesSinceUpdate.some(series => isSameSubSeries(update, series)))),
          tap(() => {
            // all stale updates have been removed - reset
            this.replacedSeriesSinceUpdate = [];
          })
        )),
        map(newPoints => {
          // group requests by series index, concat the new datapoints into one array
          const extendTracesRequests = newPoints.reduce<Map<number, ExtendTracesRequest>>((request, curr) => {
            const matchingSeriesIndices = [...this.tab.outputData.keys()]
              .filter(i => isSameSubSeries(this.tab.outputData[i], curr));
            for (const seriesIdx of  matchingSeriesIndices) {
              let seriesRequest = request.get(seriesIdx);
              if (seriesRequest === undefined) {
                seriesRequest = { x: [], y: [] };
                request.set(seriesIdx, seriesRequest);
              }
              // this modifies the ExtendTracesRequest in the map
              this.traceGenerator?.updateExtendTracesRequest(seriesRequest, curr);
            }
            return request;
          }, new Map());
          // convert empty array entries to undefined array entries
          const extendTracesRequestList = [...extendTracesRequests.values()];
          const extendTracesRequestKeys = [...extendTracesRequests.keys()];
          return {
            points: {
              x: extendTracesRequestList.map(series => series?.x ?? []),
              y: extendTracesRequestList.map(series => series?.y ?? []),
              z: extendTracesRequestList.map(series => series?.z) as (number[] | undefined)[] | undefined
            },
            series: extendTracesRequestKeys
          };
        }),
        filter(request => {
          if (request.series.length === 0) {
            // no request arrived in the last x seconds
            return false;
          }
          if (request.points.x.length === 0) {
            // no data, do nothing
            return false;
          }

          return true;
        }),
        switchMap(request => from(this.plotlyService.getPlotly()).pipe(map(plotly => ({ request, plotly }))))
      )
      .subscribe(({ request, plotly }) => {
        if (request.points.z?.some(arr => arr !== undefined) === false) {
          // all z data is missing; remove it, so plotly is not confused
          delete request.points.z;
        }
        this.noDataPresent = false;
        const config = this.tab.graphConfig;
        const plotlyElement = this.plotlyService.getInstanceByDivId('tab-plot-' + config.uuid);
        if (plotlyElement === undefined) {
          // the graph was not found, so do nothing
          return;
        }

        // does a stale virtual point need to be removed from one of the series?
        const seriesWithVirtualPoint = [];
        for (let i = 0; i < request.series.length; i++) {
          const data: any = this.tab.outputData[request.series[i]];
          if (data.x.length > 0 && data.hasVirtualEndPoint) {
            seriesWithVirtualPoint.push(i);
          }
        }
        if (seriesWithVirtualPoint.length > 0) {

          /**
           * remove the last point from these series. Use prependTraces for this, which prepends given data
           * and truncates the data array at the end based on the given maxp argument.
           * Invoke this with an empty update and maxp such that exactly one point is removed.
           */
          const emptyUpdate = seriesWithVirtualPoint.map(_ => []);
          const newArrayLengths = seriesWithVirtualPoint.map(i => this.tab.outputData[i].x!.length - 1);
          plotly.prependTraces(plotlyElement,
            {
              x: emptyUpdate,
              y: emptyUpdate
            },
            seriesWithVirtualPoint,
            {
              x: newArrayLengths,
              y: newArrayLengths,
            });
          for (const i of seriesWithVirtualPoint) {
            this.tab.outputData[request.series[i]].hasVirtualEndPoint = false;
          }
        }

        plotly.extendTraces(plotlyElement, request.points, request.series);

        void this.setLayoutRanges();
      });
  }


  private async setLayoutRanges() {
    if (!this.tab.layout) {
      return;
    }

    const data = this.tab.outputData;
    const isRelativeTime = this.tab.graphConfig.timeReference === 'RELATIVE';

    // Always use full data range for the axis so annotations remain visible.
    const boundary = determineDataEarliestAndLatest(data);
    if (boundary === null) {
      return;
    }

    // For relative time ticks, use zero as reference because data is already normalized to base date.
    const referenceStart = 0;

    // Determine x-axes to update
    const layout = this.tab.layout as Layout;

    for (const key in layout) {
      // Skip all keys that are not x-axes
      if (!key.startsWith('xaxis') || !isAxisName(key)) {
        continue;
      }

      if (this.setOfZoomedRanges.has(key)) {
        continue;
      }

      const prop = layout[key as keyof Layout] as LayoutAxis;

      prop.autorange = false;
      prop.range     = [boundary.earliest, boundary.latest];

      // For relative time mode, generate custom tick values
      if (isRelativeTime) {
        const earliest = typeof boundary.earliest === 'number' ? boundary.earliest : new Date(boundary.earliest).getTime();
        const latest = typeof boundary.latest === 'number' ? boundary.latest : new Date(boundary.latest).getTime();
        const customTicks = generateRelativeTimeTicks(earliest, latest, referenceStart);
        if (customTicks) {
          prop.tickmode = 'array';
          prop.tickvals = customTicks.tickvals;
          prop.ticktext = customTicks.ticktext;
        } else {
          // For very small ranges, let Plotly use default formatting
          delete (prop as any).tickmode;
          delete (prop as any).tickvals;
          delete (prop as any).ticktext;
        }
      } else {
        // Clear any custom tick settings when not in relative time mode
        delete (prop as any).tickmode;
        delete (prop as any).tickvals;
        delete (prop as any).ticktext;
      }
    }

    ++this.plotRevision;
  }


  private async resetLayoutRangesForDragLayer(layer: Element) {
    const group = layer.closest('g');
    if (!group) {
      return;
    }

    const axisPair = group.classList[0];
    const subplotAxisSet = getAxesFromSubplotKey(axisPair);
    const layout = this.thePlot!.layout! as Layout;

    // Find axes overlaying the axes from the subplot key
    const result: string[] = [...subplotAxisSet
      .map(axis => getAxisLongFromShortName(axis))
      .filter(axis => layout[axis as 'yaxis'].fixedrange !== true)];


    // In addition find all axes that are overlaying the axes from the subplot key
    // because they are in the same row
    const layoutAxisKeys = Object.keys(layout).filter(isAxisName);
    for (const axis of subplotAxisSet) {
      const overlaying = layoutAxisKeys.filter(
        key => (layout[key as 'yaxis']).overlaying === axis &&
          layout[key as 'yaxis'].fixedrange !== true
      );

      result.push(...overlaying);
    }


    const changeSet: { [key: string]: boolean } = {};

    result.forEach(axis => changeSet[`${axis}.autorange`] = true);

    (await this.plotlyService.getPlotly()).relayout(this.thePlot!.plotEl.nativeElement, changeSet);
  }


  /**
   * Make the underlying plotly component
   * perform a resize
   */
  public async resize() {
    if (!this.thePlot) {
      return;
    }
    const plotlyLoaded = typeof(await this.thePlot.plotly.getPlotly()) === 'object';
    if (this.tab.layout !== undefined && this.thePlot.plotEl.nativeElement !== undefined && plotlyLoaded) {
      try {
        this.thePlot.plotly.resize(
          this.thePlot.plotEl.nativeElement
        );
      } catch (e: any) {
        // probably too early to resize - just ignore
      }
      this.plotRevision++;
    }
  }

  public async refreshGraph() {
    this.markPlotRenderPending();
    const graphConfig = this.tab.graphConfig;

    this.traceGenerator  = this.generatorProviderService.getTraceGenerator(graphConfig);
    this.configGenerator = this.generatorProviderService.getConfigurationGenerator(graphConfig);
    this.layoutGenerator = this.generatorProviderService.getLayoutGenerator(graphConfig);

    await this.layoutGenerator.forTab(this.tab);

    if (this.tab.legend.length) {
      this.legendVisible.set(true);
    } else {
      this.legendVisible.set(false);
    }

    this.removeResetButtons$.next();

    if (!this.staticData) {
      // reset the data series
      this.tab.outputData = [];
    } else {
      this.noDataPresent = false;
      this.plotRevision++;
    }

    this.tab.plotlyConfig = this.configGenerator.fromGraphConfig(
      graphConfig,
      this.thingieService,
      { isForDashboardCard: this.tab._isDashboardCard },
      this.copilotSidebarService,
      this.graphAgentContextService
    );
    this.plotRevision++;

    // tell the data loader that the requested data has changed
    this.dataGraphConfig$.next(graphConfig);

    // if the thingie is loaded and there are not internalSeries then set the chart as loaded
    if (graphConfig.internalSeriesList.length < 1 && graphConfig.thingie !== undefined) {
      this.chartIsLoaded.emit();
    }
  }

  /**
   * Handle the new size of the chart view container
   */
  private async onNewChartViewAreaSize(): Promise<void> {
    void this.resize();
  }

  protected groupSeriesForLegend = (data: { legend: ILegend; series: PlotDataWithIdentification[] }): ILegendSeriesGroup[] => {
    const { legend, series } = data;
    const legendEntriesWithSeries = legend.map(({ identification, subseries, group, seriesName, seriesModificationInfo }) => ({
      group,
      seriesName,
      series: series.find(s => isSameSubSeries(s, { series: { identification }, subseries }))!,
      seriesModificationInfo
    }))
      .filter(info => info.series !== undefined);

    const legendGroups = groupBy(legendEntriesWithSeries, 'group');

    const legendGroupRows = Object.entries(legendGroups).map(([key, series]) => ({ name: key, seriesList: series }));
    return sortBy(legendGroupRows, 'name');
  };

  /**
   * Toggles the focus for the specified series on (show only this series) or
   * off (show all series).
   *
   * @param info The series to focus
   * @private
   */
  protected async _focusSeries(info: ILegendSeriesInfo): Promise<void> {
    const plotly$ = this.thePlot?.plotly.getPlotly();

    const indices: number[] = [];
    const visibilities: boolean[] = [];

    if (this.focusedGroupOrSeries === info) {
      // Reset the focus

      this.focusedGroupOrSeries = null;

      this.tab.outputData.forEach((data, idx) => {
        if (isSameSubSeries(data, info.series)) {
          // We don't need to update the visibility to `true`, because it already is
          return;
        }

        indices.push(idx);
        visibilities.push(true);
      });
    } else {
      // Set the focus to the provided series
      this.focusedGroupOrSeries = info;

      this.tab.outputData.forEach((data, idx) => {
        if (isSameSubSeries(data, info.series)) {
          if (data.visible !== true) {
            // Update of the focused element only necessary when not visible
            indices.push(idx);
            visibilities.push(true);
          }
        } else {
          // Hide all other series
          if (data.visible !== false) {
            indices.push(idx);
            visibilities.push(false);
          }
        }
      });
    }

    if (indices.length === 0) {
      // Nothing to do
      return;
    }


    // Apply the necessary updates

    const update: Partial<PlotData> = {
      visible: visibilities as any
    };

    const plotly = await plotly$;
    const graphDiv = this.thePlot?.plotlyInstance;

    plotly?.restyle(graphDiv, update, indices);
  }

  protected onMouseMove(event: MouseEvent, outputData: PlotDataWithIdentification[]) {
    this.mouseMove$.next({
      event,
      outputData
    });
  }

  // calculate shortest distance
  // https://stackoverflow.com/questions/849211/shortest-distance-between-a-point-and-a-line-segment

  private square(x: number) {
    return x * x;
  }

  private dist2(v: { x: number; y: number }, w: { x: number; y: number }) {
    return this.square(v.x - w.x) + this.square(v.y - w.y);
  }

  /**
   *
   * @param p mouse position
   * @param v start of line (x1, y1)
   * @param w end of line (x2, y2)
   * @returns distance from mouse position to this line
   */

  private distToSegmentSquared(p: { x: number; y: number }, v: { x: number; y: number }, w: { x: number; y: number }) {
    const l2 = this.dist2(v, w);
    if (l2 === 0) {
      return this.dist2(p, v);
    }
    let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t));
    return this.dist2(p, { x: v.x + t * (w.x - v.x), y: v.y + t * (w.y - v.y) });
  }

  /**
   *
   * @param p mouse position
   * @param v start of line position (x1, y1)
   * @param w end of line (x2, y2)
   * @returns square of distance from mouse position to this line
   */

  private distToSegment(p: { x: number; y: number }, v: { x: number; y: number }, w: { x: number; y: number }) {
    return Math.sqrt(this.distToSegmentSquared(p, v, w));
  }

  /**
   * Finds the closest line to the mouse position
   *
   * @param event
   * @returns The curve index of the closest line or undefined, if no line
   *    is close enough.
   */
  private searchClosestLine(event: MouseEvent) {
    const gd: any                              = ((event.currentTarget as HTMLElement).children[0]);
    const fullData: ExtendedPlotlyFullData[]   = gd._fullData;
    const fullLayout: ExtendedPlotlyFullLayout = gd._fullLayout;

    if (!fullData) {
      return;
    }

    const hoverRadius = DefaultConfig.chartView.hoverThreshold;

    const { left, top } = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const mouseXPlotDiv = event.clientX - left;
    const mouseYPlotDiv = event.clientY - top;

    const calcDistance: { distance: number; index: number }[] = [];

    fullData.forEach((
      dataItem: ExtendedPlotlyFullData,
      dataItemIndex: number
    ) => {
      const dataItemSubplot = `${dataItem.xaxis}${dataItem.yaxis}`;

      // check whether cursor is in the range of subplot
      const yAxisOffset = fullLayout._plots[dataItemSubplot].yaxis._offset;
      const yAxisOffsetWithLength = yAxisOffset + fullLayout._plots[dataItemSubplot].yaxis.length;
      const isMouseYWithinRange = mouseYPlotDiv > yAxisOffset && mouseYPlotDiv < yAxisOffsetWithLength;

      const xAxisOffset = fullLayout._plots[dataItemSubplot].xaxis._offset;
      const xAxisOffsetWithLength = xAxisOffset + fullLayout._plots[dataItemSubplot].xaxis.length;
      const isMouseXWithinRange = mouseXPlotDiv > xAxisOffset && mouseXPlotDiv < xAxisOffsetWithLength;


      if (dataItem._x?.length > 0) {
        if (isMouseXWithinRange && isMouseYWithinRange) {
          const quadtree = gd.calcdata?.[dataItemIndex]?.[0]?.t?.tree;
          let idx = [];
          if (quadtree !== undefined) {
            // search a square of radius hoverRadius around the mouse in the quadtree to get indices of points in this square
            const mouseXinAxisUnits = [mouseXPlotDiv - hoverRadius, mouseXPlotDiv + hoverRadius].map(px => fullLayout._plots[dataItemSubplot].xaxis.p2c(px - xAxisOffset));
            const mouseYinAxisUnits = [mouseYPlotDiv - hoverRadius, mouseYPlotDiv + hoverRadius].map(py => fullLayout._plots[dataItemSubplot].yaxis.p2c(py - yAxisOffset));
            idx = quadtree.range(mouseXinAxisUnits[0], mouseYinAxisUnits[0], mouseXinAxisUnits[1], mouseYinAxisUnits[1]);
          } else {
            idx = range(dataItem._x.length);
          }

          for (const i of idx) {
            // calculate distance for two consecutive points
            if (i + 1 === dataItem._x.length) {
              // run only as long as there are further valid points
              break;
            }
            const pixelPointX1 = fullLayout._plots[dataItemSubplot].xaxis.c2p(dataItem._x[i]);
            const pixelPointY1 = fullLayout._plots[dataItemSubplot].yaxis.c2p(dataItem._y[i]);

            const pixelPointX2 = fullLayout._plots[dataItemSubplot].
              xaxis.c2p(dataItem._x[i + 1]);
            const pixelPointY2 = fullLayout._plots[dataItemSubplot].
              yaxis.c2p(dataItem._y[i + 1]);

            const v = { x: pixelPointX1, y: pixelPointY1 };
            const u = { x: pixelPointX2, y: pixelPointY2 };
            // we use mouseXPlotDiv and mouseYPlotDiv, which are relative to the plotDiv,
            // but the c2p function returns coordinates relative to the draglayer,
            // which has its origin at xAxisOffset, yAxisOffset.
            // So to get the mouse coordinates relative to the draglayer,
            // subtract the xAxisOffset and yAxisOffset from the coordinates that are relative to the plotDiv.
            const p = { x: mouseXPlotDiv - xAxisOffset, y: mouseYPlotDiv - yAxisOffset };

            const result = this.distToSegment(p, v, u);

            calcDistance.push({
              distance: result,
              index: dataItemIndex
            });
          }
        }
      }
    });

    let currentMin = calcDistance.length > 0 ? calcDistance[0] : { distance: -1, index: -1 };
    for (let distanceInfo of calcDistance) {
      if (distanceInfo.distance < currentMin.distance) {
        currentMin = distanceInfo;
      }
    }

    return currentMin.distance > DefaultConfig.chartView.hoverThreshold ? -1 : currentMin.index;
  }

  /**
   * @private
   * @param info The series for which to toggle the visibility
   */
  protected async _toggleSeriesVisibility(info: ILegendSeriesInfo): Promise<void> {
    const plotly$ = this.thePlot?.plotly.getPlotly();

    // update series to newest series object for that identification - plotly might have regenerated it in the meantime
    const idx = this.tab.outputData.findIndex(data => isSameSubSeries(data, info.series));

    if (idx === -1) {
      return;
    }

    // Apply the necessary updates

    const isVisible = info.series.visible === true || info.series.visible === undefined;
    const update: Partial<PlotData> = {
      visible: !isVisible
    };

    const plotly = await plotly$;
    const graphDiv = this.thePlot?.plotlyInstance;

    plotly?.restyle(graphDiv, update, [idx]);
  }

  protected onPlotInitialized() {
    this.markPlotRenderPending();
    // Custom double-click, because plotly interferes with the default double-click
    this.thePlotHtmlElement!.nativeElement.addEventListener(
      'click',
      (ev: MouseEvent) => {
        const el = ev.target as Element;
        if (
          ev.button === 0 // Main button
          && ev.detail === 2 // at least two clicks within time frame
          && el.tagName === 'rect' && el.classList.contains('drag') // is drag layer
        ) {
          void this.resetLayoutRangesForDragLayer(el);
        }
      },
      { capture: true }
    );

    // setup a "redraw on webgl context lost" handler
    this.thePlot!.plotlyInstance!.on('plotly_webglcontextlost', () => {
      this.markPlotRenderPending();
      // emit a "different" empty object, so that the *ngFor in the template will recreate the plot component
      this.recreatePlotEvent$.next({});
    });
    void this.onNewChartViewAreaSize();
    this.plotRevision++;
  }


  protected setOfZoomedRanges = new Set<AxisName_T>();

  protected onRelayout(data: any) {
    const newRanges = new Map<string, { from?: string; to?: string }>();
    const isRelativeTime = this.tab.graphConfig.timeReference === 'RELATIVE';
    const layout = this.tab.layout as Layout | undefined;

    for (const [prop, zoomInfo] of Object.entries(data)) {
      if (prop.startsWith('xaxis')) {
        const [axis, axisProp] = prop.split('.');
        if (!newRanges.has(axis)) {
          newRanges.set(axis, {});
        }
        if (axisProp === 'autorange' && zoomInfo === true) {
          // switch to full range data - just emit an empty new range, nothing to do
          this.setOfZoomedRanges.delete(axis as AxisName_T);
        }
        const range = newRanges.get(axis)!;
        if (axisProp === 'range[0]') {
          range.from = zoomInfo as string;
          this.setOfZoomedRanges.add(axis as AxisName_T);
        }
        if (axisProp === 'range[1]') {
          range.to = zoomInfo as string;
          this.setOfZoomedRanges.add(axis as AxisName_T);
        }

      }
    }

    const relativeTimeBounds = isRelativeTime
      ? determineDataEarliestAndLatest(this.tab.outputData)
      : null;
    const relayoutChangeSet: { [key: string]: number } = {};
    let shouldUpdateTicks = false;

    for (let [axis, range] of newRanges.entries()) {
      if (isRelativeTime && layout && range.from !== undefined && range.to !== undefined) {
        const fromRangeMillis = parseFloat(range.from);
        const toRangeMillis = parseFloat(range.to);

        if (!Number.isNaN(fromRangeMillis) && !Number.isNaN(toRangeMillis)) {
          const clampedRange = clampRangeToDistinctTicks(
            fromRangeMillis,
            toRangeMillis,
            0,
            relativeTimeBounds === null
              ? undefined
              : {
                min: toMillis(relativeTimeBounds.earliest),
                max: toMillis(relativeTimeBounds.latest)
              }
          );

          range.from = `${clampedRange.start}`;
          range.to = `${clampedRange.end}`;

          if (clampedRange.wasClamped) {
            relayoutChangeSet[`${axis}.range[0]`] = clampedRange.start;
            relayoutChangeSet[`${axis}.range[1]`] = clampedRange.end;
          }

          const customTicks = generateRelativeTimeTicks(clampedRange.start, clampedRange.end, 0);
          const axisLayout = layout[axis as keyof Layout] as LayoutAxis | undefined;

          if (axisLayout && customTicks) {
            axisLayout.tickmode = 'array';
            axisLayout.tickvals = customTicks.tickvals;
            axisLayout.ticktext = customTicks.ticktext;
            shouldUpdateTicks = true;
          } else if (axisLayout && !customTicks) {
            delete (axisLayout as any).tickmode;
            delete (axisLayout as any).tickvals;
            delete (axisLayout as any).ticktext;
            shouldUpdateTicks = true;
          }
        }
      }

      for (const series of this.tab._axesMappings?.axisToSeries[axis] ?? []) {
        this.traceDataMap.get(series)?.setRange(range);
      }
    }

    if (Object.keys(relayoutChangeSet).length > 0 && this.thePlot?.plotEl.nativeElement) {
      void this.plotlyService.getPlotly().then(plotly => {
        plotly.relayout(this.thePlot!.plotEl.nativeElement, relayoutChangeSet);
      });
    }

    if (shouldUpdateTicks) {
      ++this.plotRevision;
    }

    if (this.tab.layout) {
      this.zoomButtons?.onRelayout(this.tab.layout);
    }
  }

  /**
   * Handler, triggered when a legend group is hovered.
   *
   * Highlights the series in the group.
   *
   * @private
   * @param group
   */
  protected async _onLegendGroupHover(group: ILegendSeriesGroup) {
    await this._onLegendSeriesHover(group.seriesList);
  }


  /**
   * Handler, triggered when a legend group's hover state is just ending.
   *
   * De-highlights the series in the group.
   *
   * @private
   * @param group
   */
  protected async _onLegendGroupHoverEnd(group: ILegendSeriesGroup) {
    await this._onLegendSeriesHoverEnd(group.seriesList);
  }


  /**
   * Handler, triggered when series in a legend are hovered.
   *
   * Highlights the series
   *
   * @private
   * @param info
   * @returns
   */
  protected async _onLegendSeriesHover(info: ILegendSeriesInfo[]): Promise<void> {
    if (this.ancestorIsScrolling) {
      return;
    }

    await this.legendHoverImplementation(info, StyleVariant.Highlighted);
  }


  /**
   * Handler, triggered when series in a legend's hover state is just ending.
   *
   * De-highlights the series.
   *
   * @private
   * @param info
   * @returns
   */
  protected async _onLegendSeriesHoverEnd(info: ILegendSeriesInfo[]): Promise<void> {
    if (this.ancestorIsScrolling) {
      return;
    }

    await this.legendHoverImplementation(info, StyleVariant.Normal);
  }


  /**
   * Updates the styles of traces based on the provided series information
   * and the desired style variant.
   *
   * @param info Series information on which to base the desired styles
   * @param variant The style variant to apply
   */
  private async legendHoverImplementation(
    info: ILegendSeriesInfo[],
    variant: StyleVariant
  ): Promise<void> {
    // For reducing a potential blocking time later
    const plotly$ = this.thePlot?.plotly.getPlotly();

    // Prepare the update object

    const { markerSizes, indices, lineWidth }
      = this.getHoverIndicesAndTraceStyles(info, variant);

    const update: Partial<PlotData> = {
      'marker.size': markerSizes,
      'line.width': lineWidth
    };


    // Obtain reference to graph div and plotly instance to use plotly.restyle()

    const plotly: any = await plotly$;
    const graphDiv = this.thePlot?.plotlyInstance;

    plotly?.restyle(graphDiv, update, indices);
  }


  /**
   *
   * @param info Series information on which to base the desired styles
   * @returns Marker sizes for each series, indices of the series to update
   *    and the new line width of all series provided.
   */
  private getHoverIndicesAndTraceStyles(
    info: ILegendSeriesInfo[],
    variant: StyleVariant
  ): IIndicesAndStyles {

    // Depending on variant, set the desired styles

    let annotationMarkerSize = this.annotationMarkerSize;
    let markerSize           = this.markerSizeDefault;
    let lineWidth            = this.lineSizeDefault;

    if (variant === StyleVariant.Highlighted) {
      annotationMarkerSize = this.annotationMarkerSizeHighlighted;
      markerSize           = this.markerSizeHighlighted;
      lineWidth            = this.lineSizeHighlighted;
    }


    // Determine the indices of the series to update

    const indices: number[] = [];
    const markerSizes: number[] = [];

    info.forEach(seriesInfo => {
      const idx = this.tab.outputData.findIndex(
        data => isSameSubSeries(data, seriesInfo.series)
      );

      if (idx !== -1) {
        indices.push(idx);
        if (isAnnotationSeries(seriesInfo.series.series)) {
          markerSizes.push(annotationMarkerSize);
        } else {
          markerSizes.push(markerSize);
        }
      }
    });


    return { markerSizes, indices, lineWidth };
  }


  /**
   * Focus/unfocus traces in the graph and legend
   *
   * Changes the visibility of the non-focused traces so they have less visibility
   * than the focused trace.
   *
   * @param info Series information on which to act on
   */
  protected async _focusSeriesGroup(group: ILegendSeriesGroup): Promise<void> {
    // For reducing a potential blocking time later
    const plotly$ = this.thePlot?.plotly.getPlotly();

    // Determine the indices of the series to update

    const indices: number[] = [];
    const visibilities: boolean[] = [];

    if (this.focusedGroupOrSeries === group) {
      // Remove focus from the group => reset visibilities
      this.focusedGroupOrSeries = null;
      this.tab.outputData.forEach((_, i) => {
        indices.push(i);
        visibilities.push(true);
      });
    } else {
      // Focus on the group => set visibilities
      this.focusedGroupOrSeries = group;
      this.tab.outputData.forEach((data, i) => {
        const isPartOfSelectedGroup = group.seriesList.find(
          series => isSameSubSeries(data, series.series)
        );

        indices.push(i);
        visibilities.push(isPartOfSelectedGroup ? true : false);
      });
    }


    // Prepare the update object
    const update: Partial<PlotData> = {
      visible: visibilities as any
    };


    // Obtain reference to graph div and plotly instance to use plotly.restyle()
    const plotly: any = await plotly$;
    const graphDiv = this.thePlot?.plotlyInstance;


    // Update the visibility of the traces
    plotly?.restyle(graphDiv, update, indices);
  }


  /**
   * @private
   * @param eventData
   * @returns
   */
  private async highlightCurve(eventData: {
    event: MouseEvent;
    outputData: PlotDataWithIdentification[];
  }): Promise<void> {
    if (this.ancestorIsScrolling) {
      return;
    }

    // For reducing a potential blocking time later
    const plotly$ = this.thePlot?.plotly.getPlotly();

    // Find the curve closest to the mouse position
    const closestCurve = this.searchClosestLine(eventData.event);
    if (closestCurve === undefined) {
      return;
    }

    const indices: number[] = [];
    const lineSizes: number[] = [];
    const markerSizes: number[] = [];

    eventData.outputData.forEach((output, idx) => {
      if (idx === closestCurve) {
        // Highlight - if not already
        if (output.line?.width !== this.lineSizeHighlighted) {
          indices.push(idx);
          lineSizes.push(this.lineSizeHighlighted);
          markerSizes.push(
            isAnnotationSeries(output.series)
              ? this.annotationMarkerSizeHighlighted
              : this.markerSizeHighlighted
          );
        }
      } else {
        // Remove highlight - if any

        if (output.line?.width !== this.lineSizeDefault) {
          indices.push(idx);
          lineSizes.push(this.lineSizeDefault);
          markerSizes.push(
            isAnnotationSeries(output.series)
              ? this.annotationMarkerSize
              : this.markerSizeDefault
          );
        }
      }
    });

    if (indices.length === 0) {
      return;
    }


    // Prepare the update object
    const update: Partial<PlotData> = {
      'line.width': lineSizes as any,
      'marker.size': markerSizes
    };


    // Obtain reference to graph div and plotly instance
    const plotly: any = await plotly$;
    const graphDiv = this.thePlot?.plotlyInstance;

    // Update the traces
    plotly?.restyle(graphDiv, update, indices);
  }


  protected readonly legendVisible = signal<boolean>(true);

  protected toggleLegend() {
    this.legendVisible.update(v => !v);
  }

  public isFullscreenActive = input<boolean>(false);

  /**
   * Depending on the height of the legend, adjust the visual distance to the bottom edge
   * used to calculate the height of the chart area, so space is used as desired.
   */
  private updateVisualDistanceBuffer() {
    const legendWrapper = this.legendwrappers.get(0)?.nativeElement;
    const plotWrapper = this.plotWrapper?.nativeElement;


    if (legendWrapper && plotWrapper) {
      let baseDistanceToBottom = 226; // default value, making the chart end at the bottom of the view
      let maxLegendHeightToConsider = 134; // Maximum allowed distance to bottom is 360px => (360-226)px = 134px

      if (this.isFullscreenActive()) {
        baseDistanceToBottom = 92; // in fullscreen mode, there are fewer elements, margin, etc.
        maxLegendHeightToConsider = 156; // allow more rows in fullscreen mode
      }

      const heightOfLegend = legendWrapper?.getBoundingClientRect().height ?? 0;
      const delta = Math.min(heightOfLegend, maxLegendHeightToConsider);

      plotWrapper.style.setProperty(
        '--sbi-thingie-detail-distance-buffer',
        `${baseDistanceToBottom + delta}px`
      );
    }
  }


  private disableRightClickInteractionsOnChart() {
    const plotEl = this.flexContainerAroundWrapper().nativeElement;
    plotEl.addEventListener(
      'mousedown',
      event => {
        if (event.button === 2) {
          // right click, disable context menu
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      { capture: true }
    );
  }
}

/**
 * Represents a series in the graph and encapsulates how data is loaded and processed
 * in response to zooming.
 */
class TraceData {

  /** contains fully zoomed out data for this series */
  private _fullData: IConfigLoadedSeriesData[] = [];

  /** contains fully zoomed out data for this series */
  private fullData$ = new ReplaySubject<IConfigLoadedSeriesData[]>(1);

  /** contains the currently zoomed-to range */
  private currentRange$ = new ReplaySubject<{ from?: DateTime; to?: DateTime }>(1);

  /** trigger to put data again into the generateTrace function, by retriggering the currentRange$ observable */
  private reemitCurrentRange$ = new Subject<undefined>();

  /** contains the live data that has arrived since data has been requested */
  private _liveData: IConfigLoadedSeriesData[] = [];

  /** contains the live data that has arrived since data has been requested */
  private _liveData$ = new Subject<IConfigLoadedSeriesData>();

  /** contains the processed live data */
  public liveData$ = this._liveData$.asObservable().pipe(
    delayWhen(() => this.handleAutoOffsetRequestedEvent()),
    filter((data) => data.x.length > 0),
    map(data => this.traceGenerator.generateTrace(data, this.tab))
  );

  /** triggers that a new virtual endpoint is calculated and emitted in the updateVirtualEndpoint$ observable */
  private _updateVirtualEndpoint$ = new Subject<void>();

  public updateVirtualEndpoint$ = this._updateVirtualEndpoint$.pipe(
    throttleTimeBatched(5000),
    switchMap(() => {
      const series = this.graphConfig.internalSeriesList[0];
      let lastData = this._liveData.find(data => isSameSubSeries(data, { series, subseries: 'data' }));
      if (lastData === undefined || lastData.x.length === 0) {
        lastData = this._fullData.find(data => isSameSubSeries(data, { series, subseries: 'data' }));
      }

      if (lastData === undefined || lastData.x.length === 0) {
        return EMPTY;
      }

      const virtualPointTime = this.virtualEndpointTime ?? new Date().getTime();

      // don't add the same point again
      if (ArrayUtils.endValue(lastData.x) === virtualPointTime) {
        return EMPTY;
      }

      const virtualData = {
        series,
        x: [virtualPointTime],
        y: [ArrayUtils.endValue(lastData.y)],
        subseries: 'data',
        replace: false,
        x0: this.x0,
      };
      const virtualTrace = this.traceGenerator.generateTrace(virtualData, this.tab);
      // mark this point such that it can be removed later when an update comes in
      virtualTrace.hasVirtualEndPoint = true;

      return of(virtualTrace);
    }),
  );

  public updateVirtualEndpoint() {
    if (!this.addVirtualEndpoint) {
      return;
    }

    this._updateVirtualEndpoint$.next();
  }

  /** contains the data that should be shown in the plot for this series */
  public shownData$ = this.currentRange$.pipe(
    // emit the same range again when triggerDataReprocessing$ emits
    switchMap(currentRange => merge(of(undefined), this.reemitCurrentRange$).pipe(map(() => currentRange))),
    switchMap(({ from, to }) => {
      if (from === undefined && to === undefined) {
        // fully zoomed out - show full data plus live data
        return this.fullData$.pipe(map(seriesList => seriesList.map(data => {
          const subSeriesIndexLivedata = this._liveData.findIndex(seriesData => isSameSubSeries(seriesData, data));
          if (subSeriesIndexLivedata !== -1) {
            const liveData = this._liveData[subSeriesIndexLivedata];
            const liveDataX = liveData?.x ?? [];
            const liveDataY = liveData?.y ?? [];
            data.x = data.x.concat(liveDataX);
            data.y = data.y.concat(liveDataY);
            // clear live data for this series, because if we are here, livedata is incorporated into the fullData data
            this._liveData[subSeriesIndexLivedata].x = [];
            this._liveData[subSeriesIndexLivedata].y = [];
          }
          return data;
        })));
      }

      // zoomed in
      // If denoising is enabled, help the denoising algorithm by providing a start
      // value by searching through fullData for the y value at the `from` time.
      let denoisingStartValues = [];
      if (this._fullData.length > 0 && from !== undefined) {
        const fromMillis = from.toMillis();
        denoisingStartValues = this.graphConfig.internalSeriesList.map(series => {
          if (!isScatterSeries(series) || series.valueConfig.denoising.enabled === false) {
            return undefined;
          }
          const subSeriesIndexFulldata = this._fullData.findIndex(seriesData => isSameSubSeries(seriesData, { series, subseries: 'data' }));
          if (subSeriesIndexFulldata === -1) {
            return undefined;
          }
          const fullDataSeries = this._fullData[subSeriesIndexFulldata];
          if (fullDataSeries.x.length === 0) {
            return undefined;
          }
          let i = fullDataSeries.x.findIndex(x => x > fromMillis);
          if (i === -1) {
            i = fullDataSeries.x.length;
          }
          const y1 = fullDataSeries.y[i - 1]?.value ?? fullDataSeries.y[i - 1];
          const y2 = fullDataSeries.y[i]?.value ?? fullDataSeries.y[i];
          const x1 = fullDataSeries.x[i - 1];
          const x2 = fullDataSeries.x[i];
          if (i === 0) {
            return y2;
          }
          if (i === fullDataSeries.x.length) {
            return y1;
          }
          return y1 + (y2 - y1) * (fromMillis - x1) / (x2 - x1);
        });
      }

      const sampledRequest = this.dataLoader.loadDataAsObservable(of(this.graphConfig), 1000, false, { from, to }, denoisingStartValues);

      return sampledRequest.pipe(
        switchMap(data => this.fullData$.asObservable().pipe(map(fullData => ({ fullData, data })))),
        map(({ fullData, data }) => {
          // create data to show, which is a copy of the fullData series, with the higher resolution sampled data replacing the section inside.
          // So get fullData until first point of zoomed data, then zoomed data, then rest of fullData afer last sample
          const subSeriesIndex = fullData.findIndex(seriesData => isSameSubSeries(seriesData, data));
          const fullSubseries = fullData[subSeriesIndex];
          const addFirstPoint = fullData.length > 0 && fullSubseries.x.length > 0 && (fullSubseries.x[0] < (from?.toMillis() ?? data.x[0]));
          const firstPointsX = addFirstPoint ? fullSubseries.x.filter(x => x < (from?.toMillis() ?? data.x[0])) : [];
          const firstPointsY = addFirstPoint ? fullSubseries.y.filter((_, i) => fullSubseries.x[i] < (from?.toMillis() ?? data.x[0])) : [];
          const addLastPoint = fullData.length > 0 && fullSubseries.x.length > 0 && fullSubseries.x[fullSubseries.x.length - 1] > (to?.toMillis() ?? data.x[data.x.length - 1]);
          const lastPointsX = addLastPoint ? fullSubseries.x.filter(x => x > (to?.toMillis() ?? data.x[data.x.length - 1])) : [];
          const lastPointsY = addLastPoint ? fullSubseries.y.filter((_, i) => fullSubseries.x[i] > (to?.toMillis() ?? data.x[data.x.length - 1])) : [];

          data.x = firstPointsX.concat(data.x, lastPointsX);
          data.y = firstPointsY.concat(data.y, lastPointsY);
          return [data];
        })
      );
    }),
    map(dataList => {
      if (this.addVirtualEndpoint) {
        dataList.forEach(data => {
          if (data.x.length > 0) {
            data.x.push(this.virtualEndpointTime ?? new Date().getTime());
            data.y.push(ArrayUtils.endValue(data.y));
          }
        });
      }
      return dataList;
    }),
    delayWhen(dataList => dataList.some(data => data.x.length > 0) ? this.handleAutoOffsetRequestedEvent() : of(undefined)),
    map(dataList => dataList.map(data => {
      const trace = this.traceGenerator.generateTrace(data, this.tab);
      if (this.addVirtualEndpoint && data.x.length > 0) {
        trace.hasVirtualEndPoint = true;
      }
      return trace;
    })),
  );

  /** the graph config for just this series */
  private graphConfig: IGraphConfig;

  // eslint-disable-next-line aquila/cleanup-subscriptions -- manually cleaned up in destroy
  private subscriptions = new SubSink();

  /** start point for relative time calculation */
  private x0: string | undefined;

  private addVirtualEndpoint = false;
  private virtualEndpointTime?: number;

  constructor(
    private tab: ITab,
    graphConfig: IGraphConfig,
    series: IDataSeries,
    private dataLoader: IConfigSeriesDataLoader,
    private traceGenerator: PlotlyTraceGenerator,
    private handleAutoOffsetRequestedEvent: () => Observable<boolean>,
    private sharedRelativeBaseDate?: string,
  ) {
    this.graphConfig = { ...graphConfig, internalSeriesList: [series] };
    if (series.dataItemDescription.value?.valueInfo[0]?.unit === 'yes/no') {
      // add random last point that is far in the future and therefore not visible
      this.addVirtualEndpoint = true;
    }
    this.subscriptions.sink = this.dataLoader.loadDataAsObservable(of(this.graphConfig), 1000, true)
      .subscribe({ next: data => this.setFullData(data), error: (error: unknown) => this.emitError(error) });
  }

  public destroy() {
    this.subscriptions.unsubscribe();
  }

  private emitError(error: unknown) {
    this.fullData$.error(error);
  }

  private setFullData(newData: IConfigLoadedSeriesData) {
    if (this.graphConfig.timeReference === 'RELATIVE' && this.sharedRelativeBaseDate) {
      newData.x0 = this.sharedRelativeBaseDate;
    }
    this.x0 = newData.x0;

    if (newData.seriesStopDate !== undefined) {
      // series is stopped
      this.virtualEndpointTime = DateTime.fromISO(newData.seriesStopDate).toMillis();
    }
    if (newData.replace === true) {
      const subSeriesIndex = this._fullData.findIndex(seriesData => isSameSubSeries(seriesData, newData));
      if (subSeriesIndex !== -1) {
        this._fullData[subSeriesIndex] = newData;
      } else {
        this._fullData.push(newData);
      }
      this.fullData$.next(this._fullData);
    } else {
      const subSeriesIndexLivedata = this._liveData.findIndex(seriesData => isSameSubSeries(seriesData, newData));
      if (subSeriesIndexLivedata !== -1) {
        const liveData = this._liveData[subSeriesIndexLivedata];
        liveData.x = liveData.x.concat(newData.x);
        liveData.y = liveData.y.concat(newData.y);
      } else {
        this._liveData.push(newData);
      }
      this._liveData$.next(newData);
    }
  }

  public setRange(plotlyRange: { from?: string; to?: string }) {
    // first convert plotly date to normal date
    const newRange: { from?: DateTime; to?: DateTime } = {};
    if (plotlyRange.from) {
      if (!this.x0) {
        throw new Error('zoomed but no x0 available yet');
      }
      if (this.graphConfig.timeReference === 'RELATIVE') {
        // With linear x-axis, plotlyRange values are relative milliseconds
        newRange.from = relativeLinearToDateTime(plotlyRange.from, DateTime.fromISO(this.x0));
      } else {
        newRange.from = absolutePlotlyDateToDateTime(plotlyRange.from);
      }
    }
    if (plotlyRange.to) {
      if (!this.x0) {
        throw new Error('zoomed but no x0 available yet');
      }
      if (this.graphConfig.timeReference === 'RELATIVE') {
        // With linear x-axis, plotlyRange values are relative milliseconds
        newRange.to = relativeLinearToDateTime(plotlyRange.to, DateTime.fromISO(this.x0));
      } else {
        newRange.to = absolutePlotlyDateToDateTime(plotlyRange.to);
      }
    }
    this.currentRange$.next(newRange);
  }

  /**
   * Trigger to process the received data again using the generateTrace function.
   * To be used when the offset of the series in the graph config changes
   */
  public reprocessData() {
    this.reemitCurrentRange$.next(undefined);
  }

  private getShownData$() {
    return this.shownData$;
  }
}
